import { Record, OrderedMap, List } from 'immutable';
import { Color } from 'app/models/rtc/draw/color';
import { StreamPosition } from '@engoo/realtime-protocol/persisted';
import { Resolution } from 'app/models/rtc/draw/resolution';
import { IIntPoint, TraceType } from 'app/models/rtc/draw/trace';
import { makeInt } from 'app/utils/ioTs';

export class IntPoint extends Record<IIntPoint>({
  x: makeInt(0),
  y: makeInt(0),
}) {
  public constructor(params: IIntPoint) {
    super(params);
  }
}

interface IShapeBase {
  type: TraceType,
  /**
   * Resolution of the user input at the time this shape was created
   *
   * Current resolution comes from a `switch` event. Please note
   * that the resolution can change dynamically if the surface has
   * changed its size. This mostly applies when screen-sharing a
   * window and resizing it.
   */
  resolution: Resolution,
  /**
   * List of points in coordinates limited by `resolution`
   */
  points: List<IntPoint>;
  /**
   * Radius (if circular) or width of the stroke
   */
  strokeSize: number;
}

export interface IPencilShape extends IShapeBase {
  type: typeof TraceType.Pencil;
  color: Color;
}

export class PencilShape extends Record<IPencilShape>({
  type: TraceType.Pencil,
  points: List(),
  resolution: new Resolution(),
  color: new Color(),
  strokeSize: 0,
}) {
  public constructor(params: Omit<IPencilShape, 'type'>) {
    super(params);
  }
}

export interface IEraserShape extends IShapeBase {
  type: typeof TraceType.Eraser;
}

export class EraserShape extends Record<IEraserShape>({
  type: TraceType.Eraser,
  points: List(),
  resolution: new Resolution(),
  strokeSize: 0,
}) {
  public constructor(params: Omit<IEraserShape, 'type'>) {
    super(params);
  }
}

// Add new shape types here
export type Shape =
  | PencilShape
  | EraserShape;

export type Shapes = OrderedMap<StreamPosition, Shape>;
